import React, {useState, useEffect, useCallback} from 'react';
import {collection, query, where, getDocs} from 'firebase/firestore';
import {db} from '../firebase';
import {Link, useNavigate} from 'react-router-dom';
import {
  Star,
  Search as SearchIconLucide,
  MapPin,
  DollarSign,
  Languages as LanguagesIcon,
  Clock,
  Users,
  Award,
  Image as ImageIcon,
  X,
  Sparkles,
  SlidersHorizontal,
  Globe,
  Check,
} from 'lucide-react';
import debounce from 'lodash/debounce';
import BookingModal from '../components/BookingModal';
import {useAuth} from '../contexts/AuthContext';
import {cn} from '../util/designUtil';
import { NotificationPermissionRequest } from '../components/NotificationPermissionRequest';
import Select from 'react-select';
import { getCountryOptions, getCityOptions, getCountryByName } from '../data/cities';

const INFLUENCER_TAGS = [
  'All',
  'Fitness',
  'Tech',
  'Lifestyle',
  'Fashion',
  'Beauty',
  'Gaming',
  'Food',
  'Travel',
  'Education',
  'Sports',
  'Music',
  'Art',
] as const;

interface User {
  id: string;
  slug?: string;
  name: string;
  tags: string[];
  pricePerMinute?: number;
  minCallDuration?: number;
  maxCallDuration?: number;
  imageUrl?: string;
  followers?: string;
  isAvailable?: boolean;
  rating?: number;
  ratingCount?: number;
  location?: string;
  languages?: string[];
}

const Button: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'outline' | 'solid';
    children: React.ReactNode;
  }
> = ({className, variant, children, ...props}) => (
  <button
    className={cn(
      'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      variant === 'outline'
        ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
        : variant === 'solid'
        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
        : '',
      className,
    )}
    {...props}>
    {children}
  </button>
);

const Card: React.FC<{className?: string; children: React.ReactNode}> = ({
  className,
  children,
}) => (
  <div
    className={cn(
      'rounded-lg border bg-card text-card-foreground shadow-sm',
      className,
    )}>
    {children}
  </div>
);

const CardContent: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({className, children}) => (
  <div className={cn('p-4', className)}>{children}</div>
);

const Badge: React.FC<{className?: string; children: React.ReactNode}> = ({
  className,
  children,
}) => (
  <span
    className={cn(
      'inline-block px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full',
      className,
    )}>
    {children}
  </span>
);


const SearchScreen: React.FC = () => {
  const navigate = useNavigate();
  const {user: authUser} = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>(['All']);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStar, setSelectedStar] = useState<User | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [countryFilter, setCountryFilter] = useState('');
  const [countryFilterCode, setCountryFilterCode] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);

  // Common languages for the filter
  const commonLanguages = ['English','Urdu', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Japanese', 'Korean', 'Chinese', 'Arabic', 'Hindi', 'Russian'];

  const performSearch = useCallback(async (term: string, tags: string[], filters: {country: string, city: string, minPrice: string, maxPrice: string, languages: string[]}) => {
    setLoading(true);
    setError(null);
    try {
      const usersRef = collection(db, 'users');
      let q = query(usersRef, where('isStarAccount', '==', true));

      const querySnapshot = await getDocs(q);
      const results: User[] = [];
      const lowerCaseTerm = term.toLowerCase();

      querySnapshot.forEach(doc => {
        const userData = doc.data();
        const nameMatches =
          !term || userData.name?.toLowerCase().includes(lowerCaseTerm);
        const tagKeywordsMatch =
          !term ||
          userData.tags?.some((t: string) =>
            t.toLowerCase().includes(lowerCaseTerm),
          );

        const searchCondition = nameMatches || tagKeywordsMatch;

        const categoryTagMatches =
          tags.includes('All') ||
          tags.length === 0 ||
          tags.some(tag =>
            userData.tags
              ?.map((t: string) => t.toLowerCase())
              .includes(tag.toLowerCase()),
          );

        // Location filter (country and/or city)
        const countryMatches = !filters.country || 
          userData.country?.toLowerCase() === filters.country.toLowerCase() ||
          userData.location?.toLowerCase().includes(filters.country.toLowerCase());
        const cityMatches = !filters.city ||
          userData.city?.toLowerCase() === filters.city.toLowerCase() ||
          userData.location?.toLowerCase().includes(filters.city.toLowerCase());
        const locationMatches = countryMatches && cityMatches;

        // Price filter
        const priceMatches = (!filters.minPrice || (userData.pricePerMinute && userData.pricePerMinute >= parseFloat(filters.minPrice))) &&
          (!filters.maxPrice || (userData.pricePerMinute && userData.pricePerMinute <= parseFloat(filters.maxPrice)));

        // Languages filter
        const languagesMatch = filters.languages.length === 0 ||
          (userData.languages && Array.isArray(userData.languages) && 
           filters.languages.some(lang => userData.languages.includes(lang)));

        // Skip own profile from search results
        if (authUser && doc.id === authUser.uid) return;

        if (searchCondition && categoryTagMatches && locationMatches && priceMatches && languagesMatch) {
          results.push({
            id: doc.id,
            slug: userData.slug,
            name: userData.name || '',
            tags: userData.tags || [],
            pricePerMinute: userData.pricePerMinute,
            minCallDuration: userData.minCallDuration,
            maxCallDuration: userData.maxCallDuration,
            imageUrl: userData.imageUrl,
            languages: userData.languages || [],
            location: userData.location || '',
            isAvailable: false,
          });
        }
      });

      // Check availability for each star by querying their slots
      const now = new Date();
      const batchSize = 10;
      for (let i = 0; i < results.length; i += batchSize) {
        const batch = results.slice(i, i + batchSize);
        await Promise.all(batch.map(async (star) => {
          try {
            const slotsQuery = query(
              collection(db, 'slots'),
              where('starId', '==', star.id),
              where('startTime', '>=', now),
            );
            const slotsSnap = await getDocs(slotsQuery);
            const hasFreeSlotsResult = slotsSnap.docs.some(slotDoc => {
              const slotData = slotDoc.data();
              const bookings = slotData.bookings || [];
              const slotStart = slotData.startTime.toDate();
              const slotEnd = slotData.endTime.toDate();
              const totalSlotMinutes = (slotEnd.getTime() - slotStart.getTime()) / 60000;
              const bookedMinutes = bookings.reduce((sum: number, b: any) => {
                const bStart = b.startTime.toDate ? b.startTime.toDate() : new Date(b.startTime);
                const bEnd = b.endTime.toDate ? b.endTime.toDate() : new Date(b.endTime);
                return sum + (bEnd.getTime() - bStart.getTime()) / 60000;
              }, 0);
              return bookedMinutes < totalSlotMinutes;
            });
            star.isAvailable = hasFreeSlotsResult;
          } catch {
            star.isAvailable = false;
          }
        }));
      }

      setUsers(results);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search stars. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  const debouncedSearchUsers = useCallback(debounce(performSearch, 500), [
    performSearch,
  ]);

  useEffect(() => {
    const filters = {
      country: countryFilter,
      city: cityFilter,
      minPrice,
      maxPrice,
      languages: selectedLanguages
    };
    debouncedSearchUsers(searchTerm, selectedTags, filters);
  }, [searchTerm, selectedTags, countryFilter, cityFilter, minPrice, maxPrice, selectedLanguages, debouncedSearchUsers]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleTagToggle = (tag: string) => {
    if (tag === 'All') {
      setSelectedTags(['All']);
    } else {
      setSelectedTags(prevTags => {
        const newTags = prevTags.includes(tag)
          ? prevTags.filter(t => t !== tag)
          : [...prevTags.filter(t => t !== 'All'), tag];
        return newTags.length === 0 ? ['All'] : newTags;
      });
    }
  };

  const handleIconFilterClick = (label: string) => {
    // Toggle: if clicking the same filter, close it; otherwise, open the new one
    setActiveFilter(prev => prev === label ? null : label);
  };


  const handleLanguageToggle = (language: string) => {
    setSelectedLanguages(prev => 
      prev.includes(language) 
        ? prev.filter(l => l !== language)
        : [...prev, language]
    );
  };

  const clearAllFilters = () => {
    setCountryFilter('');
    setCountryFilterCode('');
    setCityFilter('');
    setMinPrice('');
    setMaxPrice('');
    setSelectedLanguages([]);
    setSelectedTags(['All']);
    setActiveFilter(null);
  };

  const handleBookClick = (star: User) => {
    if (!authUser) {
      navigate('/login', {state: {from: '/search'}});
      return;
    }
    setSelectedStar(star);
    setIsBookingModalOpen(true);
  };

  const clearFiltersAndSearch = () => {
    setSearchTerm('');
    clearAllFilters();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <NotificationPermissionRequest />
      <div className="container mx-auto py-6 sm:py-8 px-4">
        {/* Hero Section */}
        <div className="mb-4 sm:mb-8">
          <div className="text-center mb-4 sm:mb-8">
            <h1 className="text-xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Discover Your Perfect Influencer
            </h1>
            <p className="hidden sm:block text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
              Connect with top creators across all categories. Book personalized sessions starting at just $1/minute.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-4 sm:mb-6">
            <div className="relative">
              <SearchIconLucide className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name or category..."
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 h-11 sm:h-14 text-sm sm:text-lg border-2 border-gray-200 focus:border-purple-500 rounded-xl sm:rounded-2xl shadow-lg bg-white transition-all"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 rounded-full p-2"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Category Pills - hidden on mobile */}
        <div className="hidden sm:block mb-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {INFLUENCER_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={cn(
                  "group relative px-5 py-2.5 rounded-full font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5",
                  selectedTags.includes(tag)
                    ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                )}
              >
                <span className="flex items-center gap-2 text-sm">
                  {selectedTags.includes(tag) && <Sparkles className="h-3.5 w-3.5" />}
                  {tag}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Compact Filter Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            {/* Active Filters Display */}
            <div className="flex items-center gap-2 flex-1 flex-wrap min-w-0">
              {(countryFilter || cityFilter || minPrice || maxPrice || selectedLanguages.length > 0) ? (
                <>
                  {(countryFilter || cityFilter) && (
                    <Badge className="px-2.5 py-1 bg-white border border-purple-200 text-gray-700 text-xs flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      {[cityFilter, countryFilter].filter(Boolean).join(', ')}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500"
                        onClick={() => { setCountryFilter(''); setCountryFilterCode(''); setCityFilter(''); }}
                      />
                    </Badge>
                  )}
                  {(minPrice || maxPrice) && (
                    <Badge className="px-2.5 py-1 bg-white border border-yellow-200 text-gray-700 text-xs flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      ${minPrice || '0'}-${maxPrice || '∞'}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500"
                        onClick={() => {
                          setMinPrice('');
                          setMaxPrice('');
                        }}
                      />
                    </Badge>
                  )}
                  {selectedLanguages.slice(0, 2).map((language) => (
                    <Badge
                      key={`lang-${language}`}
                      className="px-2.5 py-1 bg-white border border-blue-200 text-gray-700 text-xs flex items-center gap-1"
                    >
                      <LanguagesIcon className="h-3 w-3" />
                      {language}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500"
                        onClick={() => handleLanguageToggle(language)}
                      />
                    </Badge>
                  ))}
                  {selectedLanguages.length > 2 && (
                    <Badge className="px-2.5 py-1 bg-white border border-blue-200 text-gray-700 text-xs">
                      +{selectedLanguages.length - 2} more
                    </Badge>
                  )}
                  <button
                    onClick={clearAllFilters}
                    className="text-xs h-7 text-gray-500 hover:text-gray-700 px-2"
                  >
                    Clear all
                  </button>
                </>
              ) : (
                <span className="text-sm text-gray-500">No filters applied</span>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setActiveFilter(activeFilter ? null : 'panel')}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-xl font-medium shadow-md hover:shadow-lg transition-all",
                activeFilter
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                  : "bg-white text-gray-700 border-2 border-gray-200"
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>

          {/* Expandable Filter Panel */}
          {activeFilter && (
            <Card className="mt-4 border-2 border-gray-200 shadow-lg bg-white overflow-hidden animate-in slide-in-from-top-2">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {/* Location Filter Button */}
                  <button
                    onClick={() => handleIconFilterClick('Location')}
                    className={cn(
                      "h-10 px-4 rounded-lg border-2 hover:shadow-md transition-all justify-start flex items-center gap-2 text-sm font-medium",
                      (countryFilter || cityFilter)
                        ? "border-purple-300 bg-purple-50 text-purple-700"
                        : "border-gray-200 hover:border-gray-300 bg-white text-gray-700"
                    )}
                  >
                    <Globe className="h-4 w-4" />
                    <span>Location</span>
                    {(countryFilter || cityFilter) && (
                      <Badge className="ml-auto bg-purple-600 text-white text-xs h-5 min-w-5 flex items-center justify-center p-0 px-1.5">
                        {[countryFilter, cityFilter].filter(Boolean).length}
                      </Badge>
                    )}
                  </button>

                  {/* Price Filter Button */}
                  <button
                    onClick={() => handleIconFilterClick('Price')}
                    className={cn(
                      "h-10 px-4 rounded-lg border-2 hover:shadow-md transition-all justify-start flex items-center gap-2 text-sm font-medium",
                      (minPrice || maxPrice)
                        ? "border-yellow-300 bg-yellow-50 text-yellow-700"
                        : "border-gray-200 hover:border-gray-300 bg-white text-gray-700"
                    )}
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>Price</span>
                  </button>

                  {/* Languages Filter Button */}
                  <button
                    onClick={() => handleIconFilterClick('Languages')}
                    className={cn(
                      "h-10 px-4 rounded-lg border-2 hover:shadow-md transition-all justify-start flex items-center gap-2 text-sm font-medium",
                      selectedLanguages.length > 0
                        ? "border-blue-300 bg-blue-50 text-blue-700"
                        : "border-gray-200 hover:border-gray-300 bg-white text-gray-700"
                    )}
                  >
                    <LanguagesIcon className="h-4 w-4" />
                    <span>Languages</span>
                    {selectedLanguages.length > 0 && (
                      <Badge className="ml-auto bg-blue-600 text-white text-xs h-5 min-w-5 flex items-center justify-center p-0 px-1.5">
                        {selectedLanguages.length}
                      </Badge>
                    )}
                  </button>

                  {/* Categories Filter Button */}
                  <button
                    onClick={() => handleIconFilterClick('Categories')}
                    className={cn(
                      "h-10 px-4 rounded-lg border-2 hover:shadow-md transition-all justify-start flex items-center gap-2 text-sm font-medium",
                      !selectedTags.includes('All')
                        ? "border-pink-300 bg-pink-50 text-pink-700"
                        : "border-gray-200 hover:border-gray-300 bg-white text-gray-700"
                    )}
                  >
                    <SearchIconLucide className="h-4 w-4" />
                    <span>Categories</span>
                    {!selectedTags.includes('All') && (
                      <Badge className="ml-auto bg-pink-600 text-white text-xs h-5 min-w-5 flex items-center justify-center p-0 px-1.5">
                        {selectedTags.length}
                      </Badge>
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

            {/* Categories Filter */}
            {activeFilter === 'Categories' && (
              <Card className="mb-4 border-2 border-gray-200 bg-white rounded-xl shadow-xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Select Categories</h3>
                    <button
                      onClick={() => setActiveFilter(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
                    {INFLUENCER_TAGS.map(tag => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          onClick={() => handleTagToggle(tag)}
                          className={cn(
                            'text-sm rounded-lg px-4 py-2 transition-all duration-150 font-medium',
                            isSelected
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md hover:shadow-lg'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-purple-300',
                          )}>
                          {isSelected && <Check className="inline h-3.5 w-3.5 mr-1" />}
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Location Filter */}
            {activeFilter === 'Location' && (
              <Card className="mb-4 border-2 border-gray-200 bg-white rounded-xl shadow-xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Select Location</h3>
                    <button
                      onClick={() => setActiveFilter(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Select
                      name="countryFilter"
                      options={getCountryOptions()}
                      value={countryFilter ? { value: countryFilter, label: countryFilter, isoCode: countryFilterCode } : null}
                      onChange={(selected) => {
                        setCountryFilter(selected ? selected.value : '');
                        setCountryFilterCode(selected ? selected.isoCode : '');
                        setCityFilter('');
                      }}
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      placeholder="Select country..."
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '44px',
                          borderRadius: '0.75rem',
                          borderWidth: '2px',
                          borderColor: '#e5e7eb',
                          '&:hover': { borderColor: '#a855f7' },
                          boxShadow: 'none',
                        }),
                      }}
                    />
                    <Select
                      name="cityFilter"
                      options={countryFilterCode ? getCityOptions(countryFilterCode) : []}
                      value={cityFilter ? { value: cityFilter, label: cityFilter } : null}
                      onChange={(selected) => setCityFilter(selected ? selected.value : '')}
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isDisabled={!countryFilterCode}
                      placeholder={countryFilterCode ? "Select city..." : "Select country first..."}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '44px',
                          borderRadius: '0.75rem',
                          borderWidth: '2px',
                          borderColor: '#e5e7eb',
                          '&:hover': { borderColor: '#a855f7' },
                          boxShadow: 'none',
                        }),
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Price Filter */}
            {activeFilter === 'Price' && (
              <Card className="mb-4 border-2 border-gray-200 bg-white rounded-xl shadow-xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Price Range (per minute)</h3>
                    <button
                      onClick={() => setActiveFilter(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="text-xs text-gray-500 mb-1">Min</div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-700">$</span>
                        <input
                          type="number"
                          placeholder="0"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          className="w-full font-bold text-sm bg-transparent border-none outline-none"
                        />
                      </div>
                    </div>
                    <div className="text-gray-400 text-xs">—</div>
                    <div className="flex-1 border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="text-xs text-gray-500 mb-1">Max</div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-700">$</span>
                        <input
                          type="number"
                          placeholder="100"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          className="w-full font-bold text-sm bg-transparent border-none outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Languages Filter */}
            {activeFilter === 'Languages' && (
              <Card className="mb-4 border-2 border-gray-200 bg-white rounded-xl shadow-xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">Select Languages</h3>
                    <button
                      onClick={() => setActiveFilter(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {commonLanguages.map(language => {
                      const isSelected = selectedLanguages.includes(language);
                      return (
                        <button
                          key={language}
                          onClick={() => handleLanguageToggle(language)}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all font-medium',
                            isSelected
                              ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-300',
                          )}>
                          {isSelected && <Check className="h-3.5 w-3.5" />}
                          <span className="truncate">{language}</span>
                        </button>
                      );
                    })}
                  </div>
                  {selectedLanguages.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setSelectedLanguages([])}
                        className="w-full text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 px-3 py-2 rounded-lg font-medium transition-colors">
                        Clear All ({selectedLanguages.length})
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

        </div>

        {/* Results Section */}
        <div className="mb-3 sm:mb-6">
          <h2 className="text-base sm:text-xl font-bold text-gray-900">
            {users.length} influencer{users.length !== 1 ? 's' : ''} found
          </h2>
        </div>

        {error && (
          <div className="text-center py-6 bg-red-50 border-2 border-red-200 rounded-xl shadow-md">
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500"></div>
            <p className="mt-4 text-gray-600 font-medium text-lg">Loading influencers...</p>
          </div>
        ) : (
          <>
            {users.length === 0 && !error ? (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 mb-6">
                  <SearchIconLucide className="h-10 w-10 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">No influencers found</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  We couldn't find any influencers matching your criteria. Try adjusting your filters or search term.
                </p>
                <button
                  onClick={clearFiltersAndSearch}
                  className="border-2 border-purple-500 text-purple-700 hover:bg-purple-50 px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all">
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {users.map(starUser => ( 
                  <Card
                    key={starUser.id}
                    className="group overflow-hidden border border-gray-200 hover:border-purple-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 bg-white rounded-xl">
                    <Link to={`/star/${starUser.slug || starUser.id}`}>
                      <div className="aspect-[4/5] relative overflow-hidden">
                        {starUser.imageUrl ? (
                          <img
                            src={starUser.imageUrl}
                            alt={starUser.name}
                            className="object-cover w-full h-full transform group-hover:scale-110 transition-transform duration-300"
                            loading="lazy"
                            onError={(e) => {
                              const img = e.currentTarget;
                              // Retry once with cache-busting param
                              if (!img.dataset.retried) {
                                img.dataset.retried = 'true';
                                img.src = starUser.imageUrl + (starUser.imageUrl!.includes('?') ? '&' : '?') + 'retry=1';
                              } else {
                                // Fallback to placeholder
                                img.style.display = 'none';
                                img.parentElement?.classList.add('flex', 'items-center', 'justify-center', 'bg-gradient-to-br', 'from-gray-100', 'to-gray-200');
                                const placeholder = document.createElement('div');
                                placeholder.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
                                img.parentElement?.appendChild(placeholder);
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                            <ImageIcon className="h-16 w-16 text-gray-300" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        {starUser.followers && (
                          <div className="absolute top-2 right-2 bg-white/95 backdrop-blur rounded-full px-2 py-1 text-xs font-semibold shadow-md">
                            {starUser.followers} followers
                          </div>
                        )}
                        
                        {starUser.isAvailable ? (
                          <div className="absolute bottom-2 left-2 bg-green-500 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-md flex items-center gap-1">
                            <div className="h-1.5 w-1.5 bg-white rounded-full animate-pulse" />
                            Available
                          </div>
                        ) : (
                          <div className="absolute bottom-2 left-2 bg-gray-700/90 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-md">
                            Unavailable
                          </div>
                        )}
                      </div>
                    </Link>

                    <CardContent className="p-2 sm:p-3">
                      <Link to={`/star/${starUser.slug || starUser.id}`}>
                        <div className="flex justify-between items-start mb-1 sm:mb-2">
                          <h3 className="text-xs sm:text-sm font-bold text-gray-900 group-hover:text-purple-600 transition-colors truncate">
                            {starUser.name}
                          </h3>
                          {typeof starUser.rating === 'number' && typeof starUser.ratingCount === 'number' && (
                            <div className="flex items-center bg-yellow-50 rounded-full px-2 py-0.5 flex-shrink-0">
                              <Star className="h-3 w-3 text-yellow-500 fill-current mr-0.5" />
                              <span className="text-xs font-bold text-gray-900">{starUser.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      </Link>

                      <div className="flex flex-wrap gap-1 mb-2">
                        {starUser.tags.slice(0, 2).map(tag => (
                          <Badge
                            key={tag}
                            className="px-2 py-0.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200 capitalize text-[10px]">
                            {tag}
                          </Badge>
                        ))}
                        {starUser.tags.length > 2 && (
                          <Badge className="px-2 py-0.5 bg-gray-50 text-gray-500 border border-gray-200 text-[10px]">
                            +{starUser.tags.length - 2}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center text-xs text-gray-500 mb-2 sm:mb-3 gap-1.5 flex-wrap">
                        {starUser.location && (
                          <div className="flex items-center gap-0.5 truncate">
                            <Globe className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{starUser.location}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between items-center pt-1.5 sm:pt-2 border-t border-gray-100">
                        <div className="flex flex-col">
                          <span className="text-sm sm:text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                            {starUser.pricePerMinute != null ? `$${Number(starUser.pricePerMinute).toFixed(2)}/min` : 'Price not set'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleBookClick(starUser)}
                          className={cn(
                            "px-3 sm:px-4 py-1 sm:py-1.5 rounded-lg text-xs font-semibold shadow-sm hover:shadow-md transition-all",
                            starUser.pricePerMinute != null && authUser
                              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          )}
                          disabled={starUser.pricePerMinute == null || !authUser}
                          title={!authUser ? 'Please log in to book' : ''}>
                          Book
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {selectedStar && (
        <BookingModal
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedStar(null);
          }}
          starId={selectedStar.id}
        />
      )}
    </div>
  );
};

export default SearchScreen;
